import type { ReactNode } from 'react';
import { ClipboardList } from 'lucide-react';
import type { IpdTaskView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Button } from '@/components/ui/primitives';
import { displayStatus, taskIsPending } from './record-task';
import { OrderBadges, TaskStatusChip } from './TaskChip';
import { readingLines, taskTitle } from './task-labels';
import { useWitnesses } from './task-queries';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

export function TaskDetailsDialog({ task, now, onClose }: { task: IpdTaskView; now: number; onClose: () => void }) {
  const { user, can } = useAuth();
  const pending = taskIsPending(task);
  const status = displayStatus(task, now);
  const named = task.recordedByName !== undefined || task.witnessName !== undefined;
  const needsNames = !pending && !named && can('emar.administer') && Boolean((task.recordedBy && task.recordedBy !== user?.id) || task.witnessUserId);
  const staff = useWitnesses(needsNames);
  const nameOf = (userId: string | null, known: string | null | undefined) => {
    if (!userId) return null;
    if (userId === user?.id) return tr('ipd', 'io_task_you');
    return known ?? staff.data?.find((member) => member.id === userId)?.name ?? tr('ipd', 'io_task_another_staff_member');
  };
  const lines = readingLines(task.taskKind, task.value);

  return (
    <Dialog
      open
      onClose={onClose}
      icon={ClipboardList}
      title={task.order.label}
      description={taskTitle(task)}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {tr('ipd', 'close')}
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <TaskStatusChip status={status} />
          <OrderBadges priority={task.order.priority} isHighAlert={task.order.isHighAlert} state={task.order.state} />
        </div>
        <dl className="divide-y">
          <Row label={tr('ipd', 'io_task_scheduled')}>{dateTime(task.scheduledAt)}</Row>
          {pending && <Row label={tr('ipd', 'io_task_recorded')}>{tr('ipd', 'io_task_not_recorded_yet')}</Row>}
          {task.recordedAt && <Row label={tr('ipd', 'io_task_recorded_at')}>{dateTime(task.recordedAt)}</Row>}
          {task.recordedBy && <Row label={tr('ipd', 'io_task_recorded_by')}>{nameOf(task.recordedBy, task.recordedByName)}</Row>}
          {task.witnessUserId && <Row label={tr('ipd', 'witnessed_by')}>{nameOf(task.witnessUserId, task.witnessName)}</Row>}
          {task.reasonCode && <Row label={tr('ipd', 'io_task_reason')}>{tr('ipd', `io_reason_${task.reasonCode}`)}</Row>}
          {task.reason && <Row label={tr('ipd', 'io_task_reason_text')}>{task.reason}</Row>}
          {task.doseGiven && <Row label={tr('ipd', 'io_task_dose_given')}>{task.doseGiven}</Row>}
          {task.site && <Row label={tr('ipd', 'site')}>{task.site}</Row>}
          {lines.map((line) => (
            <Row key={line.field} label={line.label}>
              <span className="tabular">{line.text}</span>
            </Row>
          ))}
          {task.notes && <Row label={tr('ipd', 'notes')}>{task.notes}</Row>}
        </dl>
      </div>
    </Dialog>
  );
}
